'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, usePathname } from 'next/navigation';

import {
	buildRoutePattern,
	reportClientError,
} from '@naumu/analytics/posthog/error-reporting';
import { getClientAnalytics } from '@naumu/analytics/analytics/client-analytics';

import { ErrorScreen } from '@naumu/ui/components/error-screen';

interface RouteErrorViewProps {
	/** The error Next.js handed to the route's `error.tsx`. */
	error: Error & { digest?: string };
	/** Next.js reset - re-renders the segment without a full page reload. */
	reset: () => void;
	title?: string;
	description?: string;
}

/**
 * Shared body of every route-level `error.tsx` in the marketing app.
 *
 * twin-of: apps/web/src/modules/error/components/route-error-view.tsx
 * Kept as a copy rather than a shared component because it needs
 * `next/navigation`, which `@naumu/ui` bans - the two apps each own their
 * router. Keep the two files identical apart from the ErrorScreen import.
 *
 * Next.js App Router boundaries catch render/effect throws inside a segment.
 * React swallows those - they never reach `window.onerror`, so PostHog's
 * exception autocapture cannot see them. This reports them by hand with the
 * route PATTERN attached, then renders the DS error screen whose primary
 * action is Next's own `reset()`.
 *
 * The route is a pattern, never the filled path. `usePathname()` on its own is
 * customer data - a space slug is built from the customer's own space name -
 * so `useParams()` is used to swap each dynamic value back for its `:name`.
 *
 * The analytics events reuse the existing `global` source rather than adding
 * registry entries: the route detail already travels on the reported exception,
 * and the crash-screen funnel is meant to be read as one number.
 */
export const RouteErrorView = ({
	error,
	reset,
	title,
	description,
}: RouteErrorViewProps) => {
	const pathname = usePathname();
	const params = useParams();
	const reportedRef = useRef<unknown>(null);

	const route = useMemo(
		() => buildRoutePattern(pathname, params),
		[pathname, params],
	);

	useEffect(() => {
		// One report per distinct error object; React can re-run this effect when
		// the boundary re-renders with the same error.
		if (reportedRef.current === error) return;
		reportedRef.current = error;
		reportClientError(error, { route });
	}, [error, route]);

	const handleView = useCallback(() => {
		getClientAnalytics().track('global_error_viewed', {
			source: 'global',
			digest: error.digest,
		});
	}, [error.digest]);

	const handleReset = useCallback(() => {
		getClientAnalytics().track('global_error_reload_clicked', { source: 'global' });
		reset();
	}, [reset]);

	const handleBack = useCallback(() => {
		getClientAnalytics().track('global_error_back_clicked', { source: 'global' });
		if (typeof window === 'undefined') return;
		if (window.history.length > 1) {
			window.history.back();
			return;
		}
		window.location.href = '/';
	}, []);

	const handleYellAtUs = useCallback(() => {
		getClientAnalytics().track('global_error_yell_at_us_clicked', { source: 'global' });
	}, []);

	return (
		<ErrorScreen
			title={title}
			description={description}
			digest={error.digest}
			reloadLabel="Try again"
			onReload={handleReset}
			onBack={handleBack}
			onView={handleView}
			onYellAtUsClick={handleYellAtUs}
		/>
	);
};
